import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { toDateFromLocaleStringDate } from '../../common/dateTimeHelper';
import useFormatter from '../../common/useFormatter';
import { TooltipItem } from '../../components/stats/TooltipItem';
import { StageType } from '../../layout/stage/stage.helper';
import TooltipWrapper from '../../layout/tooltip/TooltipWrapper';
import {
  analyzeGateUsageSchedule,
  analyzeGateUsageScheduleCustomThresholds,
  analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  analyzeGateUsageScheduleWaitTimeLateThreshold,
  analyzeGateUsageScheduleWaitTimeWarningThreshold,
} from '../store/analyze.state';
import { scheduleHoveredJobId } from './schedule.state';
import { WaitTimeClassificationModel } from './waitTimeClassificationModel';

export type TimelineJobTooltipProps = {
  stageId: StageType;
};

const TimelineJobTooltip: React.FC<TimelineJobTooltipProps> = props => {
  const { t } = useTranslation('simulation');
  const formatter = useFormatter();
  const hoveredJobId = useRecoilValue(scheduleHoveredJobId);
  const scheduleLoadable = useRecoilValue(analyzeGateUsageSchedule);
  const warningThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeWarningThreshold,
  );
  const lateThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeLateThreshold,
  );
  const criticalThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  );
  const customThresholds = useRecoilValue(
    analyzeGateUsageScheduleCustomThresholds,
  );

  if (!hoveredJobId || !scheduleLoadable) return null;

  // Find the job data
  const schedule = scheduleLoadable;
  let jobData = null;
  let resourceData = null;

  for (const resource of schedule.resources || []) {
    const appointment = resource.assignments.find(
      a => a.job.id === hoveredJobId,
    );
    if (appointment) {
      jobData = appointment;
      resourceData = resource;
      break;
    }
  }

  if (!jobData || !resourceData) return null;

  // Calculate metrics
  const startTime = toDateFromLocaleStringDate(jobData.startTime);
  const completionTime = toDateFromLocaleStringDate(jobData.completionTime);
  const deadline = toDateFromLocaleStringDate(jobData.job.deadline);

  const waitTimeSeconds = Math.max(
    0,
    (startTime.getTime() - deadline.getTime()) / 1000,
  );
  const workDuration = (completionTime.getTime() - startTime.getTime()) / 1000;

  // Use the new classification model
  const classificationModel = new WaitTimeClassificationModel(
    warningThreshold,
    lateThreshold,
    criticalThreshold,
    customThresholds,
  );
  const classification = classificationModel.classify(waitTimeSeconds);

  const waitTimeFormatted =
    formatter.formatTimespanAs24String(waitTimeSeconds).fullString;
  const workDurationFormatted =
    formatter.formatTimespanAs24String(workDuration).fullString;

  return (
    <TooltipWrapper stageId={props.stageId}>
      <div className="min-w-tooltip bg-app-panel-dark/90 backdrop-blur backdrop-filter">
        {/* Job Header */}
        <div
          className="text-menu-text flex items-center gap-2 px-3 py-2"
          style={{
            backgroundColor: classification.threshold.backgroundColor,
            // color: getBestContrastColor(
            //   classification.threshold.backgroundColor,
            // ),
          }}
        >
          <div className="bg-app-panel-dark text-menu-active rounded px-2 py-1 text-xs">
            {resourceData.id}
          </div>
          <div className="flex-1 text-base font-bold">{jobData.job.id}</div>
        </div>
        {/* Metrics */}
        <div className="flex flex-col">
          <TooltipItem
            title={t`Status`}
            value={classification.threshold.label}
            backgroundColor={classification.threshold.backgroundColor}
            color={classification.threshold.color}
            classNameLabel="bg-menu/20 text-menu-text/80"
            classNameValue="bg-transparent"
            style={{
              color: classification.threshold.color,
            }}
          />
          {/* <TooltipItem
            title={t`Severity`}
            value={classification.threshold.severity.toUpperCase()}
            backgroundColor={classification.threshold.backgroundColor}
            color={classification.threshold.color}
            classNameLabel="bg-menu/20 text-menu-text/80"
            classNameValue="bg-transparent"
          /> */}
          {/* <TooltipItem
            title={t`Action Required`}
            value={classification.threshold.actionRequired}
            classNameLabel="bg-menu/20 text-menu-text/80"
            classNameValue="bg-transparent"
          /> */}
          <div className="flex w-full gap-2">
            <TooltipItem
              title={t`Start Time`}
              value={format(startTime, 'MMM dd, HH:mm')}
              classNameLabel="bg-menu/20 text-menu-text/80"
              classNameValue="bg-transparent"
              className="flex-1"
            />
            <TooltipItem
              title={t`Completion Time`}
              value={format(completionTime, 'MMM dd, HH:mm')}
              classNameLabel="bg-menu/20 text-menu-text/80"
              classNameValue="bg-transparent"
              className="flex-1"
            />
          </div>
          <TooltipItem
            title={t`Deadline`}
            value={format(deadline, 'MMM dd, HH:mm')}
            classNameLabel="bg-menu/20 text-menu-text/80"
            classNameValue="bg-transparent"
          />
          <div className="flex w-full gap-2">
            <TooltipItem
              title={t`Work Duration`}
              value={workDurationFormatted}
              classNameLabel="bg-menu/20 text-menu-text/80"
              classNameValue="bg-transparent"
              className="flex-1"
            />
            <TooltipItem
              title={t`Wait Time`}
              value={waitTimeFormatted}
              backgroundColor={classification.threshold.backgroundColor}
              color={classification.threshold.color}
              classNameLabel="bg-menu/20 text-menu-text/80"
              classNameValue="bg-transparent"
              className="flex-1"
            />
          </div>

          <div className="flex w-full gap-2">
            {jobData.dockingDuration > 0 && (
              <TooltipItem
                title={t`Docking Gap`}
                value={
                  formatter.formatTimespanAs24String(jobData.dockingDuration)
                    .fullString
                }
                classNameLabel="bg-menu/20 text-menu-text/80"
                classNameValue="bg-transparent"
                className="flex-1"
              />
            )}
            {jobData.undockingDuration > 0 && (
              <TooltipItem
                title={t`Undocking Gap`}
                value={
                  formatter.formatTimespanAs24String(jobData.undockingDuration)
                    .fullString
                }
                classNameLabel="bg-menu/20 text-menu-text/80"
                classNameValue="bg-transparent"
                className="flex-1"
              />
            )}
          </div>
        </div>
      </div>
    </TooltipWrapper>
  );
};

export default TimelineJobTooltip;
